import { useContext } from 'react';
import { AgricultureToastType } from 'app/agriculture/AgricultureToast/agriculture_toast_service';
import { Dialog } from 'app/agriculture/components/Dialog/Dialog';
import { ServicesContext } from 'app/items/shared/context_interface';
import { useHerdUpdateMutation } from 'app/agriculture/mutations/herds/useHerdUpdateMutation';
import { useHerd } from 'app/agriculture/queries/herds/useHerd';
import { useRouteActions } from 'app/agriculture/router/useRouteActions';
import { MoveIcon } from 'app/agriculture/icons';
import { StatRelationship } from 'app/agriculture/components/StatRelationship/StatRelationship';
import { AnimalIcon } from 'app/agriculture/icons/components';
import { useTimelineCursorTime } from 'app/agriculture/AgTimeline/context/TimelineContext';
import { format, isPast } from 'date-fns';
import { usePaddock } from 'app/agriculture/queries/entities/usePaddock';
import { useSnapshot } from 'app/agriculture/queries/snapshots/useSnapshot';

interface HerdMoveDialogProps {
    herdId: string;
    paddockId: string;
}

export function HerdMoveDialog({ herdId, paddockId }: HerdMoveDialogProps) {
    const { agricultureToastService } = useContext(ServicesContext);
    const time = useTimelineCursorTime();
    const routeActions = useRouteActions();
    const herd = useHerd({ herdId });
    const snapshot = useSnapshot({ snapshotType: 'Herd', parentId: herdId, shelfLife: null });
    const newPaddock = usePaddock(paddockId);
    const oldPaddock = usePaddock(snapshot?.paddockId);

    const timeText = time && isPast(time) ? `at ${format(time, 'dd/MM/yyyy hh:mm a')}` : 'now';

    const { mutate: updateHerd } = useHerdUpdateMutation({
        onSuccess: () => {
            agricultureToastService
                .showToast({
                    title: `${herd.name} moved:`,
                    subtext: `${oldPaddock?.name} ⮕ ${newPaddock?.name}`,
                    type: AgricultureToastType.SUCCESS,
                    variant: 'simple',
                })
                .catch((e) => {
                    console.error(e);
                });
        },
        onError: (error) => {
            console.error('Error moving herd', error);
            agricultureToastService
                .showToast({
                    title: 'Failed to move herd:',
                    subtext: 'Something went wrong',
                    type: AgricultureToastType.ERROR,
                    variant: 'simple',
                })
                .catch((e) => {
                    console.error(e);
                });
        },
    });

    return (
        <Dialog
            icon={
                <AnimalIcon herdType={herd.herdType} animalSpecies={herd.animalSpecies} size={24} />
            }
            title="Herd Move"
            className="HerdMoveDialog"
            actions={{
                primaryAction: {
                    id: 'herd-move-submit',
                    text: 'Move Herd',
                    startIcon: <MoveIcon />,
                    onClick: () => {
                        routeActions.goto(herdId, { type: 'Herd', replace: true });
                        updateHerd({
                            id: herdId,
                            snapshot: {
                                totalAnimals: snapshot?.totalAnimals ?? 0,
                                paddockId: paddockId,
                                timestamp:
                                    time && isPast(time)
                                        ? time.toISOString()
                                        : new Date().toISOString(),
                            },
                        });
                    },
                },
            }}
            handleClose={() => {
                routeActions.goto(herdId, { type: 'Herd', replace: true });
            }}
        >
            <p>
                Are you sure you want to move <strong id="herd-move-name">{herd.name}</strong>{' '}
                {timeText}?
            </p>

            <StatRelationship
                id="herd-move-relationship"
                arrowDirection="right"
                leftStatDisplay={{ name: 'From paddock', value: oldPaddock?.name }}
                rightStatDisplay={{ name: 'To paddock', value: newPaddock?.name }}
            />
        </Dialog>
    );
}
